"use client";
import Card from "@/components/primitives/card";
import Timestamp from "../game/timestamp";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import {
  GamePickActivity,
  OddVariant,
  PickType,
  getPickAlignment,
  pickTypeToLineType,
} from "common";
import TeamLegend from "../team/legend";
import StatComparisonChart from "../game/detail/stat-comparison-chart";
import { createPickComparison } from "./activity-data";
import TeamDisplay from "../team/display";
import OddsRow from "../game/odds/row";
import Button from "../primitives/button";
import { createGameDetailSlug } from "@/utils/url";
import React from "react";
import Avatar from "@/components/primitives/avatar";
import * as Collapsible from "@radix-ui/react-collapsible";
import Badge from "@/components/primitives/badge";
import PickGroup from "./group";
import { ALL_ODD_OPTIONS } from "@/data/sports/options";
import Select from "@/components/primitives/select";
import Icon from "@/components/primitives/icon";
import TeamLogo from "../team/logo";
import ReactTimeAgo from "react-time-ago";
import { formatUnits } from "@/utils";
import Link from "next/link";
import OddsStatusChip from "../game/odds/status-chip";
import { createProfileSlug } from "@/utils/url";
import StaticOddsCell from "../game/odds/static-cell";
import { pickToOdd } from "@/utils/picks";
import { useWebSocketMessage } from "@/utils/hooks/websocket-message";
import useFetch from "@/utils/newFetch";
import { useLocalDate } from "@/utils/hooks/local-date";
import PickChip from "../game/detail/pick-chip";
import { useGame } from "@/utils/providers/game";
import RelativeTime from "../relative-time";

function PickActivity({
  // game,
  detail,
}: {
  // game: GamePickActivity;
  detail?: boolean;
}) {
  const ctx = useGame();
  const game = ctx.game;
  const wsGamePickUpdates = useWebSocketMessage("game_picks_updated") || [];
  const [selectedOdds, setSelectedOdds] = React.useState<OddVariant>("all");
  const baseUrl: string = `${process.env.NEXT_PUBLIC_API_URL}/sports/${game.league}/games/${game.id}/picks/activity`;
  const params: URLSearchParams = new URLSearchParams();
  if (selectedOdds !== "all") {
    params.append("odd", selectedOdds);
  }
  const request: string = `${baseUrl}?${params}`;

  const [fetchState, fetchData] = useFetch<any | null>(null, request);
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);
  // React.useEffect(() => {
  //   const matchingUpdate = wsGamePickUpdates.find(
  //     (update: { gameId: number }) => Number(update.gameId) === game.id
  //   );
  //   if (matchingUpdate) {
  //     fetchData();
  //   }
  // }, [wsGamePickUpdates, game.id, fetchData]);
  const data = fetchState.data;
  console.log("pick activity data", fetchState.data);
  if (data) {
    console.log("activity feed:", data);

    const awayTeam = data.teams[0];
    const homeTeam = data.teams[1];
    return (
      <Card
        className="!pb-0 !pt-4 gap-0 min-w-[301px]"
        header={{
          title: (
            <div className="flex items-center gap-2 flex-wrap">
              <div>
                {awayTeam.name}{" "}
                <span className="text-title-lg-medium">Vs.</span>{" "}
                {homeTeam.name} Picks
              </div>
              {!detail && (
                <Timestamp
                  type="full"
                  className="text-secondary-400 text-sm"
                  dateTime={game.dateTime}
                  platform={game.platform}
                />
              )}
            </div>
          ),
          action: detail ? (
            <TeamLegend awayTeam={awayTeam} homeTeam={homeTeam} />
          ) : (
            <Button
              variant="card"
              href={createGameDetailSlug(game.league, game.id)}
            >
              View Game
            </Button>
          ),
        }}
      >
        {!detail && <TeamLegend awayTeam={awayTeam} homeTeam={homeTeam} />}

        <StatComparisonChart
          awayColor={awayTeam.primaryColor}
          homeColor={homeTeam.primaryColor}
          data={createPickComparison(game)}
        />
        {!detail && (
          <div className="mb-5 flex flex-col gap-y-2 md:gap-y-0">
            <div className="flex flex-col justify-start items-start md:flex-row md:justify-between md:items-center flex-wrap">
              <TeamDisplay
                sport={game.league}
                team={awayTeam}
                variant="location-name"
                size="md"
              />
              <OddsRow hideLogo alignment="away" game={game} />
            </div>
            <div className="flex flex-col justify-start items-start md:flex-row md:justify-between md:items-center flex-wrap">
              <TeamDisplay
                sport={game.league}
                team={homeTeam}
                variant="location-name"
                size="md"
              />
              <OddsRow hideLogo alignment="home" game={game} />
            </div>
          </div>
        )}
        {/* <PickActivityFeed data={data} detail={detail}></PickActivityFeed> */}
        <Collapsible.Root defaultOpen={detail} className="group/collapsible">
          <div className="hidden group-data-[state=open]/collapsible:block mb-6">
            <hr className="border-surface-300 border-4 rounded-full" />
          </div>
          <div className="flex justify-between items-start flex-wrap gap-y-4">
            <div className="flex justify-start items-center gap-2 gap-y-4 flex-wrap">
              <PickChip
                showAvatars
                totalPicks={ctx.game.totalPicks}
                avatars={ctx.game.avatars || []}
                totalBettors={ctx.game.totalBettors || 0}
                totalPicksForSale={ctx.game.totalPicksForSale}
                totalComments={ctx.game.discussion?.total_comments || 0}
                side="right"
              />
            </div>
            <div className="hidden group-data-[state=open]/collapsible:block">
              <Select
                label="odds"
                variant="card"
                options={ALL_ODD_OPTIONS}
                value={selectedOdds}
                onValueChange={(v) => setSelectedOdds(v as OddVariant)}
              />
            </div>
          </div>
          <Collapsible.Content>
          <ScrollArea.Root
            className={`w-full ${data.picks && data.picks.length >= 3 ? "h-72" : "max-h-72"}`}
          >
            <ScrollArea.Viewport className="w-full h-full">
            <div className="flex flex-col mt-4">
              {data.picks?.map((pick, i) => (
                <Item key={i} pick={pick}></Item>
              ))}
            </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 bg-light transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-secondary rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
            
          </Collapsible.Content>
          {data.picks?.length ? (
            <Collapsible.Trigger className="w-full h-14">
              <div className="flex flex-col w-full">
                <div className="text-label-medium uppercase text-dark flex flex-row items-center justify-center w-full">
                  <div className="flex items-center gap-2 group-data-[state=open]/collapsible:hidden">
                    <span>View Pick Activity</span>
                    <Icon name="caret-down" size="sm"></Icon>
                  </div>
                  <div className="hidden items-center gap-2 group-data-[state=open]/collapsible:flex">
                    <span>Show Less</span>
                    <Icon name="caret-up" size="sm"></Icon>
                  </div>
                </div>
              </div>
            </Collapsible.Trigger>
          ) : (
            <div className="w-full h-8"></div>
          )}
        </Collapsible.Root>
      </Card>
    );
  }
}

function Item({ pick }: { pick: any; }) {
  console.log("pick feed item is: ", pick);
  const { date, timeZone } = useLocalDate(pick.createdAt);
  const ctx = useGame();
  const game = ctx.game;
  const alignment = getPickAlignment(pick.type);
  const line = pickTypeToLineType(pick.type);

  console.log("converted pick item date:", date);
  // console.log("converted pick item date string:", dateString);
  console.log("pick created at:", pick.createdAt);

  if (pick.bettor) {
    console.log("works");
    return (
      <div className="flex flex-col desktop-grid:flex-row justify-between items-start desktop-grid:items-center mt-1">
        <div className="flex flex-row gap-2 max-w-[24rem]">
          <Link href={createProfileSlug(pick.bettor.displayName)}>
            <Avatar img={pick.bettor.avatarUrl}></Avatar>
          </Link>
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2 flex-wrap">
              <Link
                href={createProfileSlug(pick.bettor.displayName)}
                className="text-title-sm-medium text-dark"
              >
                {pick.bettor.displayName}
              </Link>
              <div className="text-title-sm text-secondary">
                <span>
                  {formatUnits(pick.bettor.totalUnits)} Units (Last 25 Picks)
                </span>
              </div>
              {/* {pick.salePrice > 0 && (
              <div className="text-title-sm-bold uppercase text-primary-300">
                For Sale
              </div>
            )} */}
            </div>
            {pick.comment && (
              <div className="text-sm text-dark">{pick.comment}</div>
            )}
            <div className="text-title-sm text-secondary">
              <RelativeTime date={pick.createdAt} />
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center w-full justify-center desktop-grid:justify-end desktop-grid:max-w-[9.5625rem] gap-4 bg-surface-600 desktop-grid:bg-transparent rounded-md my-4">
          {ctx.closed && (
            <div className="flex">
              {pick.outcome !== undefined ? (
                <OddsStatusChip outcome={pick.outcome} />
              ) : undefined}
            </div>
          )}
          <div className="flex">
            <StaticOddsCell
              alignment={alignment === 0 ? "away" : "home"}
              game={game}
              odd={pickToOdd(pick)}
              hideCheckmark={true}
              hidePickStatus={true}
              color="neutral"
            />
          </div>

          <div className="flex justify-center items-center h-12 min-w-[68px]">
            {pick.type === PickType.Over ? (
              <div className="flex justify-center items-center gap-1 pr-4">
                <Icon name="arrow-up" className="fill-primary-400" size="xs" />
                <div className="text-label-lg-bold text-secondary-300 uppercase">
                  Over
                </div>
              </div>
            ) : pick.type === PickType.Under ? (
              <div className="flex justify-center items-center gap-1 pr-4">
                <Icon name="arrow-down" className="fill-hot" size="xs" />
                <div className="text-label-lg-bold text-secondary-300 uppercase">
                  Under
                </div>
              </div>
            ) : (
              <TeamLogo
                team={alignment === 0 ? game.awayTeam : game.homeTeam}
                size="md"
              ></TeamLogo>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default PickActivity;
